import { z } from 'zod';
/**
 * Contrat partagé de la **vue d'état de l'architecture** (ADR-0037). La dérivation des états est
 * une fonction **pure et testée** — « la donnée pilote l'état » (ADR-0037 §2) : ni l'API ni le
 * front ne réinventent les seuils, ils appellent les mêmes helpers. La `SERVICE_TOPOLOGY` est
 * l'unique source de vérité des blocs et de leurs liens, partagée entre le payload de l'API et
 * (incrément 2) le schéma SVG.
 */
/**
 * Quatre états de service (ADR-0037 §2) : `vert`/`jaune`/`orange`/`rouge`. `DORMANT` est un
 * cinquième état **neutre** (gris) réservé aux nœuds non activés — les nœuds GPU externes affichés
 * « dormants » tant qu'ils ne sont pas provisionnés (ADR-0037 §2, ADR-0022/0035). Il ne participe
 * pas à la logique de sévérité : `worstStatus` ne le retient jamais face à un état d'exploitation.
 */
export const ServiceStatus = {
    OK: 'OK', // vert — sonde satisfaite
    WARN: 'WARN', // jaune — alerte mineure
    IMMINENT: 'IMMINENT', // orange — problème imminent
    DOWN: 'DOWN', // rouge — indisponible
    DORMANT: 'DORMANT', // gris — non activé, hors up/down (neutre)
};
export const serviceStatusSchema = z.nativeEnum(ServiceStatus);
/**
 * Sévérité croissante. `DORMANT` est **sous** `OK` : un nœud dormant ne doit jamais dominer l'état
 * agrégé d'un groupe qui contient un vrai service (`worstStatus`), et n'apparaît que s'il est seul.
 */
const SERVICE_STATUS_SEVERITY = {
    [ServiceStatus.DORMANT]: 0,
    [ServiceStatus.OK]: 1,
    [ServiceStatus.WARN]: 2,
    [ServiceStatus.IMMINENT]: 3,
    [ServiceStatus.DOWN]: 4,
};
/**
 * Renvoie l'état le plus sévère d'un ensemble. Sans argument → `OK` : l'absence de signal négatif
 * est un état sain par défaut (un groupe vide ne doit pas s'afficher en alerte).
 */
export function worstStatus(...statuses) {
    // Ensemble vide → OK (défaut sain). Sinon on réduit **sur les valeurs réelles**, sans plancher
    // artificiel : un unique `DORMANT` doit rester `DORMANT` et non remonter à `OK`.
    if (statuses.length === 0) {
        return ServiceStatus.OK;
    }
    return statuses.reduce((worst, current) => SERVICE_STATUS_SEVERITY[current] > SERVICE_STATUS_SEVERITY[worst] ? current : worst);
}
// --- Seuils de dérivation (ADR-0037 §2). Purs et testés : la donnée pilote l'état. ---
/**
 * Âge du battement du worker au-delà duquel il est jugé mort (ms). Le heartbeat est écrit toutes
 * les ~15 s avec un TTL de 60 s (voir `WORKER_HEARTBEAT_*`) : au-delà de 180 s (douze intervalles
 * manqués, clé expirée depuis longtemps) le worker ne consomme plus rien.
 */
export const WORKER_HEARTBEAT_DOWN_MS = 180_000;
/** Retard « problème imminent » (orange) : plus de six intervalles manqués. */
export const WORKER_HEARTBEAT_IMMINENT_MS = 90_000;
/** Retard « alerte mineure » (jaune) : trois intervalles manqués, le worker prend du retard. */
export const WORKER_HEARTBEAT_WARN_MS = 45_000;
/**
 * État du worker déduit de l'âge de son dernier battement (ADR-0037 §3). `null` (aucune clé en
 * base, worker jamais vu) → `DOWN` : c'est le seul signal fiable qu'un worker **consomme**
 * réellement, au-delà de « Valkey est joignable ».
 */
export function deriveWorkerStatus(heartbeatAgeMs) {
    if (heartbeatAgeMs === null || heartbeatAgeMs > WORKER_HEARTBEAT_DOWN_MS) {
        return ServiceStatus.DOWN;
    }
    if (heartbeatAgeMs > WORKER_HEARTBEAT_IMMINENT_MS) {
        return ServiceStatus.IMMINENT;
    }
    if (heartbeatAgeMs > WORKER_HEARTBEAT_WARN_MS) {
        return ServiceStatus.WARN;
    }
    return ServiceStatus.OK;
}
/** Backlog d'une file au-delà duquel l'attente devient un problème imminent (orange). */
export const QUEUE_IMMINENT_WAITING = 500;
/** Backlog d'une file au-delà duquel on lève une alerte mineure (jaune). */
export const QUEUE_WARN_WAITING = 100;
/**
 * État d'une file déduit du nombre de jobs **en attente** (ADR-0037 §2). Un worker vivant mais
 * débordé se voit à l'accumulation du backlog, pas au heartbeat.
 */
export function deriveQueueStatus(waiting) {
    if (waiting > QUEUE_IMMINENT_WAITING) {
        return ServiceStatus.IMMINENT;
    }
    if (waiting > QUEUE_WARN_WAITING) {
        return ServiceStatus.WARN;
    }
    return ServiceStatus.OK;
}
/**
 * État binaire up/down pour les services à sonde d'atteignabilité (PostgreSQL `SELECT 1`, Valkey
 * `PING`, Garage `HeadBucket`) — ADR-0037 §2. La granularité fine (latence, % disque) viendra
 * quand les sondes correspondantes existeront ; ici on ne ment pas sur ce qu'on mesure.
 */
export function deriveUpDownStatus(reachable) {
    return reachable ? ServiceStatus.OK : ServiceStatus.DOWN;
}
/** Identifiants des 8 blocs. Figés : le payload de l'API et le schéma SVG s'y alignent. */
export const SERVICE_NODE_IDS = [
    'caddy',
    'web',
    'api',
    'worker',
    'postgres',
    'valkey',
    'garage',
    'external-gpu',
];
/**
 * Les 8 blocs et leurs relations. Données pures, sans rendu (ADR-0037 §5 : SVG maison, incrément 2).
 * Les liens décrivent le sens du flux : le navigateur traverse Caddy → web/API, puis API et worker
 * s'appuient sur les datastores ; le worker **dispatche** vers les nœuds GPU externes (ADR-0022/0035).
 */
export const SERVICE_TOPOLOGY = {
    nodes: [
        { id: 'caddy', label: 'Caddy', role: 'proxy' },
        { id: 'web', label: 'Interface web', role: 'web' },
        { id: 'api', label: 'API', role: 'api' },
        { id: 'worker', label: 'Worker', role: 'worker' },
        { id: 'postgres', label: 'PostgreSQL', role: 'database' },
        { id: 'valkey', label: 'Valkey', role: 'cache' },
        { id: 'garage', label: 'Garage', role: 'storage' },
        { id: 'external-gpu', label: 'Nœuds GPU externes', role: 'gpu', external: true },
    ],
    links: [
        { from: 'caddy', to: 'web' },
        { from: 'caddy', to: 'api' },
        { from: 'web', to: 'api' },
        { from: 'api', to: 'postgres' },
        { from: 'api', to: 'valkey' },
        { from: 'api', to: 'garage' },
        { from: 'worker', to: 'postgres' },
        { from: 'worker', to: 'valkey' },
        { from: 'worker', to: 'garage' },
        { from: 'worker', to: 'external-gpu' },
    ],
};
// --- Heartbeat du worker : clé et cadence partagées (ADR-0037 §3). ---
/**
 * Clé Valkey du battement du worker. Déclarée **une seule fois** ici : le worker l'écrit, l'API la
 * lit — un littéral dupliqué finirait par diverger (CLAUDE.md, contrats partagés).
 */
export const WORKER_HEARTBEAT_KEY = 'ddp:worker:heartbeat';
/** Cadence d'écriture du battement (ms). */
export const WORKER_HEARTBEAT_INTERVAL_MS = 15_000;
/**
 * TTL de la clé (s). Court exprès : si le worker meurt, la clé disparaît d'elle-même et l'âge
 * devient `null` → `DOWN`, sans dépendre d'une horloge partagée pour juger la péremption.
 */
export const WORKER_HEARTBEAT_TTL_SECONDS = 60;
// --- Payload de la vue d'état (ADR-0037). ---
/** État d'un bloc à un instant donné. `detail` porte un contexte lisible (français). */
export const serviceHealthNodeSchema = z.object({
    id: z.enum(SERVICE_NODE_IDS),
    status: serviceStatusSchema,
    detail: z.string().optional(),
});
/**
 * Réponse de `GET /architecture-health` : l'état de chaque bloc + l'horodatage de la mesure (pour
 * situer une valeur potentiellement en cache côté client, comme la vue monitoring plateforme).
 */
export const architectureHealthSchema = z.object({
    services: z.array(serviceHealthNodeSchema),
    generatedAt: z.coerce.date(),
});
//# sourceMappingURL=monitoring.js.map